import { useEffect, useRef, useState } from 'react';

interface PdfExportMenuProps {
  busy?: boolean;
  /** Dark app bar (phone) vs light toolbar (desktop). */
  tone?: 'light' | 'dark';
  onDownload: () => void;
  onSendEmail: () => void;
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        fill="currentColor"
        d="M12 3a1 1 0 0 1 1 1v9.17l2.59-2.58a1 1 0 1 1 1.41 1.42l-4.3 4.29a1 1 0 0 1-1.4 0l-4.3-4.3a1 1 0 1 1 1.41-1.41L11 13.17V4a1 1 0 0 1 1-1Zm-7 14a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Z"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <path
        fill="currentColor"
        d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 2v.2l8 5.3 8-5.3V7H4Zm16 10V10.4l-7.4 4.9a1 1 0 0 1-1.2 0L4 10.4V17h16Z"
      />
    </svg>
  );
}

/** Icon trigger + dropdown: Download PDF · Send to email. */
export default function PdfExportMenu({
  busy,
  tone = 'light',
  onDownload,
  onSendEmail,
}: PdfExportMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className={`pdfe-export-menu${tone === 'dark' ? ' is-dark' : ''}${
        open ? ' is-open' : ''
      }`}
      ref={rootRef}
    >
      <button
        type="button"
        className="pdfe-export-trigger"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Export PDF"
        title="Export"
        onClick={() => setOpen((v) => !v)}
      >
        <DownloadIcon />
        <span className="pdfe-export-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="pdfe-export-dropdown" role="menu">
          <button
            type="button"
            role="menuitem"
            className="pdfe-export-option"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              onDownload();
            }}
          >
            <DownloadIcon />
            <span>Download PDF</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="pdfe-export-option"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              onSendEmail();
            }}
          >
            <MailIcon />
            <span>Send to email</span>
          </button>
        </div>
      )}
    </div>
  );
}
